import type { FC } from "react";
import { useNavigate } from "react-router";
import { TikTokAdsBudgetMode, validateTikTokAdsDraft } from "shared";
import { useModal } from "../common/modal/Modal";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { LinkGen } from "../LinkGen";
import { ModalConfirmation } from "../settings/ModalConfirmation";
import {
	objectiveLabel,
	useTikTokCampaignWizard,
} from "./TikTokCampaignWizardContext";
import { useTikTokAdsDraftActions } from "./useTikTokCampaigns";

const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
	<div
		style={{
			display: "flex",
			justifyContent: "space-between",
			gap: 16,
			padding: "6px 0",
			borderBottom: "1px solid var(--bs-border-color, #dee2e6)",
		}}
	>
		<span className="text-secondary">{label}</span>
		<span style={{ textAlign: "right", fontWeight: 500 }}>{value}</span>
	</div>
);

export const StepTikTokReview: FC = () => {
	const { draft, prevStep } = useTikTokCampaignWizard();
	const { saveDraft, publishDraft } = useTikTokAdsDraftActions();
	const { open } = useModal();
	const navigate = useNavigate();

	const errors = validateTikTokAdsDraft(draft);
	const isBusy = saveDraft.isPending || publishDraft.isPending;
	const isRetry = Boolean(draft.tiktokCampaignId);
	const budgetPeriod =
		draft.budgetMode === TikTokAdsBudgetMode.DAILY ? "daily" : "lifetime";
	const publishLabel = isRetry ? "Retry publish" : "Create campaign";
	const publishingLabel = isRetry ? "Retrying…" : "Creating…";

	const handleSaveDraft = async () => {
		try {
			await saveDraft.mutateAsync(draft);
			navigate(LinkGen.TikTokAds.Index());
		} catch {
			// The mutation's onError already surfaced an alert.
		}
	};

	const publish = async () => {
		try {
			await saveDraft.mutateAsync(draft);
			await publishDraft.mutateAsync(draft.id);
			navigate(LinkGen.TikTokAds.Index());
		} catch {
			// The mutation's onError already surfaced an alert.
		}
	};

	// Publishing commits a budget to TikTok, so it goes behind a confirmation.
	const handlePublish = () => {
		const prompt = isRetry
			? `Retry publishing this campaign with a ${budgetPeriod} budget of ${draft.budgetAmount}? It stays paused until you enable it.`
			: `Create this campaign with a ${budgetPeriod} budget of ${draft.budgetAmount}? It will be created paused, so no spend starts until you enable it.`;
		open(ModalConfirmation, {
			prompt,
			confirmText: publishLabel,
			variant: "primary",
			onConfirm: publish,
		});
	};

	return (
		<Card>
			<Card.Header>Review</Card.Header>
			<Card.Body>
				<SummaryRow label="Campaign" value={draft.name} />
				<SummaryRow label="Objective" value={objectiveLabel(draft.objective)} />
				<SummaryRow
					label="Budget"
					value={`${draft.budgetAmount} ${
						draft.budgetMode === TikTokAdsBudgetMode.DAILY
							? "per day"
							: "lifetime"
					}`}
				/>
				<SummaryRow label="Starts" value={draft.scheduleStart} />
				<SummaryRow label="Ends" value={draft.scheduleEnd ?? "No end date"} />
				<SummaryRow
					label="Locations"
					value={`${draft.locationIds.length} selected`}
				/>
				<SummaryRow label="Ages" value={draft.ageGroups.join(", ")} />
				<SummaryRow label="Optimization" value={draft.optimizationGoal} />
				<SummaryRow label="Billing" value={draft.billingEvent} />
				<SummaryRow label="Call to action" value={draft.callToAction} />
				{draft.instantFormPageId ? (
					<SummaryRow label="Instant Form" value={draft.instantFormPageId} />
				) : (
					<>
						{draft.pixelId && (
							<SummaryRow label="Pixel" value={draft.pixelId} />
						)}
						{draft.optimizationEvent && (
							<SummaryRow
								label="Optimization event"
								value={draft.optimizationEvent}
							/>
						)}
						<SummaryRow
							label="Landing page"
							value={draft.landingPageUrl || "—"}
						/>
					</>
				)}

				{errors.length > 0 && (
					<div className="alert alert-danger" style={{ marginTop: 16 }}>
						<strong>Fix these before publishing:</strong>
						<ul className="mb-0">
							{errors.map((error) => (
								<li key={error}>{error}</li>
							))}
						</ul>
					</div>
				)}

				<p className="text-secondary" style={{ marginTop: 16 }}>
					Campaigns are created paused in TikTok. Nothing spends until you
					enable it from the dashboard or TikTok Ads Manager.
				</p>

				<div style={{ display: "flex", gap: 8 }}>
					<Button variant="secondary" onClick={prevStep} disabled={isBusy}>
						Back
					</Button>
					<Button
						variant="secondary"
						onClick={handleSaveDraft}
						disabled={isBusy}
					>
						Save draft
					</Button>
					<Button
						onClick={handlePublish}
						disabled={isBusy || errors.length > 0}
					>
						{publishDraft.isPending ? publishingLabel : publishLabel}
					</Button>
				</div>
			</Card.Body>
		</Card>
	);
};
